import { useMemo } from "react"
import { api } from "~/utils/api"
import {
  type IncomingOrdersMapValue,
  type PreOrderMapValue,
  type PurchaseOrdersMapValue,
  type SalesMapValue,
} from "./useCollectionTableData"
import { combineOrdersMaps } from "~/utils/orderMapUtils"
import type { VariantMatch } from "~/hooks/useVariantMatches"
import { resolveComparisonVariantMatches } from "~/utils/resolveComparisonVariantMatches"

type Props = {
  variantIds: string[]
  collectionIds: number[]
  // Subset of collectionIds whose end date has passed. Their remaining demand is the
  // committed future pre-orders, mirroring the overview tab / MultipleCollectionsTable.
  endedCollectionIds?: number[]
  warehouseId?: string
  channel?: string
  externalCollectionIds?: string[]
  variantMatchesMap?: Map<string, VariantMatch[]>
}

export type ContributingMatch = {
  match: string
  similarity: number
}

export type CollectionComparisonTableData = {
  preOrderMap: Map<string, PreOrderMapValue>
  salesMap: Map<string, SalesMapValue>
  forecastMap: Map<string, number>
  // variantId → future pre-orders tagged to ended collections (see endedCollectionIds)
  endedFuturePreOrdersMap: Map<string, number>
  ordersMap: Map<string, number>
  incomingOrdersMap: Map<string, IncomingOrdersMapValue> | undefined
  purchaseOrdersMap: Map<string, PurchaseOrdersMapValue> | undefined
  matchedVariantsSet: Set<string>
  directVariantsSet: Set<string>
  contributingMatchesMap: Map<string, ContributingMatch[]>
  allMatchesMap: Map<string, VariantMatch[]>
}

/** Sales + remaining forecast + future pre-orders of ended collections — the same
 *  "total demand" the collection overview shows, so tile comparisons line up with it. */
export function sumComparisonDemand(data: CollectionComparisonTableData): number {
  let total = 0
  for (const v of data.salesMap.values()) total += v.sales
  for (const v of data.forecastMap.values()) total += v
  for (const v of data.endedFuturePreOrdersMap.values()) total += v
  return total
}

export default function useCollectionComparisonTableData({
  variantIds,
  collectionIds,
  endedCollectionIds = [],
  warehouseId,
  channel,
  externalCollectionIds,
  variantMatchesMap,
}: Props) {
  const enabled = variantIds.length > 0 && collectionIds.length > 0
  const externalCollectionIdsEnabled = (externalCollectionIds ?? []).length > 0

  const { data: preOrderMap, isLoading: preOrderIsLoading } =
    api.preOrder.getPreordersForCollectionByVariants.useQuery(
      { collectionIds, warehouseId, channel },
      { enabled },
    )

  const { data: forecastMap, isLoading: forecastIsLoading } =
    api.forecasts.getVariantForecastForCollections.useQuery(
      { collectionIds, warehouseId, channel },
      { enabled },
    )

  const { data: salesMap, isLoading: salesIsLoading } =
    api.sales.getSalesForCollectionPeriodOnVariant.useQuery(
      { collectionIds, warehouseId, channel },
      { enabled },
    )

  const endedEnabled = enabled && endedCollectionIds.length > 0
  const { data: endedPreOrderMap, isLoading: endedPreOrderIsLoading } =
    api.preOrder.getPreordersForCollectionByVariants.useQuery(
      { collectionIds: endedCollectionIds, warehouseId, channel },
      { enabled: endedEnabled },
    )

  const { data: incomingOrdersMap, isLoading: incomingOrdersIsLoading } =
    api.incomingOrders.getIncomingOrdersForCollectionsByVariants.useQuery(
      { externalCollectionIds: externalCollectionIds ?? [], warehouseId },
      { enabled: externalCollectionIdsEnabled },
    )

  const { data: purchaseOrdersMap, isLoading: purchaseOrdersIsLoading } =
    api.purchaseOrder.getPurchaseOrdersForCollectionsByVariants.useQuery(
      { collectionIds, warehouseId, excludeDraft: true },
      { enabled: enabled },
    )

  const isLoading =
    preOrderIsLoading ||
    forecastIsLoading ||
    salesIsLoading ||
    endedPreOrderIsLoading ||
    incomingOrdersIsLoading ||
    purchaseOrdersIsLoading

  const ordersMap = useMemo(
    () => combineOrdersMaps(incomingOrdersMap, purchaseOrdersMap),
    [incomingOrdersMap, purchaseOrdersMap],
  )

  const endedFuturePreOrdersMap = useMemo(() => {
    const map = new Map<string, number>()
    for (const [variantId, v] of endedPreOrderMap ?? []) {
      if (v.futurePreorders > 0) map.set(variantId, v.futurePreorders)
    }
    return map
  }, [endedPreOrderMap])

  const {
    resolvedSalesMap: resolvedSalesMap,
    resolvedForecastMap: resolvedForecastMap,
    resolvedPreOrderMap: resolvedPreOrderMap,
    resolvedOrdersMap: resolvedOrdersMap,
    matchedVariantsSet,
    directVariantsSet,
    contributingMatchesMap,
    allMatchesMap,
  } = useMemo(() => {
    return resolveComparisonVariantMatches({
      variantIds,
      variantMatchesMap,
      input: { salesMap, forecastMap, preOrderMap, ordersMap },
    })
  }, [variantMatchesMap, salesMap, forecastMap, preOrderMap, ordersMap, variantIds])

  const data = useMemo(() => {
    const next = {
      preOrderMap: resolvedPreOrderMap ?? new Map(),
      salesMap: resolvedSalesMap ?? new Map(),
      forecastMap: resolvedForecastMap ?? new Map(),
      endedFuturePreOrdersMap,
      ordersMap: resolvedOrdersMap ?? new Map(),
      incomingOrdersMap,
      purchaseOrdersMap,
      matchedVariantsSet,
      directVariantsSet,
      contributingMatchesMap,
      allMatchesMap,
    } satisfies CollectionComparisonTableData
    return next
  }, [
    resolvedPreOrderMap,
    resolvedSalesMap,
    resolvedForecastMap,
    resolvedOrdersMap,
    endedFuturePreOrdersMap,
    incomingOrdersMap,
    purchaseOrdersMap,
    matchedVariantsSet,
    directVariantsSet,
    contributingMatchesMap,
    allMatchesMap,
  ])

  return { data, isLoading }
}
